import React, {ChangeEvent, useEffect, useState} from 'react'
import styles from '../styles/Reviews.module.scss'
import cn from 'classnames'
import { Field, Form, Formik, FormikValues } from 'formik'
import { ReactComponent as VeryUnhappyIcon } from '../public/very_unhappy.svg'
import { ReactComponent as UnhappyIcon } from '../public/unhappy.svg'
import { ReactComponent as NeutralIcon } from '../public/neutral.svg'
import { ReactComponent as HappyIcon } from '../public/happy.svg'
import { ReactComponent as VeryHappyIcon } from '../public/very_happy.svg'
import reactImageSize from 'react-image-size'
import { useMutation } from '@apollo/client'
import { INSTANT_REVIEW_MUTATION } from '../apollo/mutation/instant_review'
import Resizer from 'react-image-file-resizer'
import { Business, Step } from '../pages/ReviewFormPage'
import { useLocation } from 'react-router-dom'

interface MoodStepProps {
  setReviewId: (id: number) => void
  setStep: (step: Step) => void
  setMood: (mood: number) => void
  onUpdateFormValues: (name: string, value: string) => void

  business: Business
}

export const MoodStep: React.FC<MoodStepProps> = ({ setReviewId, setStep, business, onUpdateFormValues, setMood }) => {
  const { search } = useLocation()
  const [submitFormLoading, setSubmitForm] = useState(false)
  const [fileObj, setFileObj] = useState([])
  const [fileArray, setFileArray] = useState([])

  const [instantReview, { loading: instantReviewLoading }] = useMutation(INSTANT_REVIEW_MUTATION)

  useEffect(() => {
    if (business?.businessFeatures && !business?.businessFeatures?.instantReviewComment && !business?.businessFeatures?.instantReviewRating) {
      setStep('thank-you-happy')
    }
    // eslint-disable-next-line
  }, [business?.businessFeatures])

  const redirectId = new URLSearchParams(search).get('redirectId')
  const userId = new URLSearchParams(search).get('userId')

  const fileToDataUri = (file: unknown) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      resolve(event?.target?.result)
    };
    reader.readAsDataURL(file as Blob)
  })

  const resizeFile = (file: unknown) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        // @ts-ignore
        file,
        530,
        530,
        "JPEG",
        100,
        0,
        (uri) => {
          resolve(uri)
        },
        'file'
      )
    })

  const onSubmit = async (values: FormikValues) => {
    setSubmitForm(true)
    let images = []
    if (values?.images) {
      for (const file of Array.from(values.images)) {
        let resizedFile = file

        const fileUrl = await fileToDataUri(file)

        let imgWidth
        await reactImageSize(fileUrl)
          .then(({width}: {width: string}) => {
            imgWidth = width
          }).catch(() => {
            console.log('Image upload error')
          })

        // @ts-ignore
        if ((imgWidth && imgWidth > 530) || (file?.size > 1048576 && imgWidth < 530)) {
          resizedFile = await resizeFile(file)
        }

        // @ts-ignore
        images.push(new File([resizedFile], resizedFile.name, { type: resizedFile.type }))
      }
    }

    const moodRating = values?.mood

    let sharingConsent = values.sharingConsent
    if (moodRating > 3 && !values?.firstName) {
      sharingConsent = true
    } else if (moodRating < 4) {
      sharingConsent = false
    }

    await instantReview({
      variables: {
        input: {
          businessId: +business?.id,
          rating: moodRating,
          review: values.review || '',
          images: images.length ? images : undefined,
          firstName: values.firstName || null,
          redirectId: redirectId ? +redirectId : null,
          userId: userId ? +userId : null,
          sharingConsent,
        }
      }}
    ).then(async (data) => {
      setReviewId(data.data?.instantReview)
      // if (!business?.useContactMeFormInstantReviews && formik.values.mood < 4) {
      //   setNotContact(true)
      // }
      if (values.firstName) {
        onUpdateFormValues('firstName', values.firstName)
      }
      if (!business?.useContactMeFormInstantReviews && moodRating! < 4) {
        setStep('not-contact-me-unhappy')
      } else {
        if (moodRating! < 4) {
          setStep('thank-you-unhappy')
        } else {
          onUpdateFormValues('review', values.review)
          setStep('thank-you-happy')
        }
      }
      return data
    }).catch((e) => {
      console.log('save instant review error', e)
    })
    setSubmitForm(false)
  }

  const removeImage = (index: number, formik: any) => {
    const newImgArr = formik.values.images
    // @ts-ignore
    newImgArr?.splice(index, 1)
    formik.setFieldValue('images', newImgArr)
  }

  const emodjiList = [
    {
      component: <VeryUnhappyIcon />,
      name: 'very_unhappy',
    },
    {
      component: <UnhappyIcon />,
      name: 'unhappy',
    },
    {
      component: <NeutralIcon />,
      name: 'neutral',
    },
    {
      component: <HappyIcon />,
      name: 'happy',
    },
    {
      component: <VeryHappyIcon />,
      name: 'very_happy',
    },
  ]

  const onMoodChoose = async (rating: number, formik: any) => {
    setMood(rating)
    await formik.setFieldValue('mood', rating)
  }

  const uploadMultipleFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const newArr = [
      ...fileObj,
      e.currentTarget.files
    ]

    // @ts-ignore
    setFileObj(newArr)

    // @ts-ignore
    for (let i = 0; i < fileObj?.[0]?.length; i++) {
      const url = URL.createObjectURL(fileObj[0][i])
      // @ts-ignore
      setFileArray([...fileArray, url])
    }
  }

  const businessFeatures = business?.businessFeatures

  const getBtnDisabled = (values: FormikValues) => {
    const formikValues = values.values
    const mood = formikValues?.mood
    return submitFormLoading
      || (businessFeatures?.instantReviewRating && !mood && mood !== 0)
      || (businessFeatures?.instantReviewComment && !formikValues?.review)
      || instantReviewLoading
  }

  return (
    <div className={styles.section}>
      {
        businessFeatures?.instantReviewRating ? (
          <div className={styles.section_question}>
            How did we make you feel?
          </div>
        ) : null
      }

      <Formik
        initialValues={{
          review: '',
          images: undefined,
          mood: undefined,
          firstName: '',
          sharingConsent: true,
        }}
        onSubmit={onSubmit}
      >
        {
          props => (
            <>
              {
                businessFeatures?.instantReviewRating ? (
                  <div className={styles.emodji_container}>
                    {
                      emodjiList.map((item, index) => {
                        const emodjiName = `section_emodji_${item.name}`
                        return (
                          <div className={cn(styles.section_emodji, styles[emodjiName],
                            (index + 1) === props.values.mood ? styles[`${emodjiName}_selected`] : '')}
                               onClick={() => !instantReviewLoading && onMoodChoose(index + 1, props)}
                               key={emodjiName}
                          >
                            {item.component}
                          </div>
                        )
                      })
                    }
                  </div>
                ) : null
              }

              {
                businessFeatures?.instantReviewComment ? (
                  <div className={styles.section_question}>
                    Please tell us why?
                  </div>
                ) : null
              }



              <Form onSubmit={props.handleSubmit}>
                {
                  businessFeatures?.instantReviewComment ? (
                    <Field
                      name='review'
                      value={props.values.review}
                      component='textarea'
                      placeholder='Share your thought...'
                      onChange={props.handleChange}
                      className='data-hj-allow'
                      required={true}
                    />
                  ) : null
                }

                {
                  businessFeatures?.instantReviewComment && businessFeatures?.instantReviewImageUpload ? (
                    <Field
                      name="images"
                      className='data-hj-allow'
                    >
                      {
                        // @ts-ignore
                        ({ field }) => {
                          return (
                            // @ts-ignore
                            <div className={cn(styles.upload_container, props.values.images?.length && styles.upload_container_margin)}>
                              <div className={styles.img_preview}>
                                {
                                  props.values.images && Array.from(props.values.images)?.map((file: any, index) => {
                                    // @ts-ignore
                                    const url = URL.createObjectURL(file)
                                    return (
                                      <div className={cn(styles.files_cloud_container, styles.files_cloud_container_margin)}>
                                        <img src={url} alt={file?.name} />
                                        <span className={styles.remove_img} onClick={() => {
                                          removeImage(index, props)
                                        }}/>
                                      </div>
                                    )
                                  })
                                }
                              </div>

                              <label className={cn(styles.files, props.values.images && styles.files_margin)}>
                                <div className={cn(styles.files_cloud, props.values.images && styles.files_cloud_container)}>
                                  <img src="/upload-cloud.svg" alt="Upload cloud"/>
                                </div>
                                {!props.values.images && <span>Upload images</span>}
                                <input
                                  type="file"
                                  onChange={(event) => {
                                    uploadMultipleFiles(event)
                                    if (props.values.images) {
                                      // @ts-ignore
                                      props.setFieldValue('images', [...props.values?.images, ...event.currentTarget.files])
                                    } else {
                                      // @ts-ignore
                                      props.setFieldValue('images', [...event?.currentTarget?.files])
                                    }
                                  }}
                                  className='data-hj-allow'
                                  multiple
                                />
                              </label>
                            </div>
                          )
                        }
                      }
                    </Field>
                  ) : null
                }

                {
                  businessFeatures?.instantReviewName ? (
                    <label htmlFor="firstName" className={cn(styles.section_question, styles.mood_label)}>
                      <div>Name</div>
                      <Field
                        label='Name'
                        name='firstName'
                        placeholder='Optional'
                        value={props.values.firstName}
                        onChange={props.handleChange}
                        className={cn(styles.custom_input, 'data-hj-allow')}
                        type='text'
                      />
                    </label>
                  ) : null
                }

                {
                  props.values.mood && props.values.mood > 3
                  && props.values.review.length
                  && props.values.firstName ? (
                    <label className={styles.agree_checkbox}>
                      <Field type='checkbox' name='sharingConsent' />
                      I agree for my comments & name to be <br/>
                      shared by the business on their Facebook <br/>
                      & Instagram.
                    </label>
                  ) : null
                }

                <button className={styles.submit} type='submit' disabled={getBtnDisabled(props)}>
                  Next
                </button>
              </Form>
            </>
          )
        }
      </Formik>
    </div>
  )
}

export default MoodStep
